import React, { useState } from "react";
import { PowerSkillLastTestResponse } from "../../typings/powerskill";
import { powerSkillService } from "../../services/powerskill.service";
import Button from "../UI/Button";
import PowerSkillAiRecommendation from "./PowerSkillAiRecommendation";
import PowerSkillPdfDownload from "./PowerSkillPdfDownload";
import { Card, CardHeader } from "../UI/Card";
import { ChevronDown, ChevronUp, Clock, RefreshCcw, CheckCircle2, AlertCircle, Loader2, Hourglass } from "lucide-react";
import { getAnswerOptionLabels } from "../../utils/powerskill";

interface PowerSkillTestResultProps {
  testResult: PowerSkillLastTestResponse;
  onBackToHome: () => void;
}

const PowerSkillTestResult: React.FC<PowerSkillTestResultProps> = ({
  testResult: initialTestResult,
  onBackToHome,
}) => {
  const [testResult, setTestResult] = useState<PowerSkillLastTestResponse>(initialTestResult);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showAnswers, setShowAnswers] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const test = testResult.data.test;

  React.useEffect(() => {
    let intervalId: any;
    let startTime = Date.now();

    if (!test.ai_generated) {
      intervalId = setInterval(async () => {
        const elapsedTime = (Date.now() - startTime) / 1000;
        if (elapsedTime >= 30) {
          clearInterval(intervalId);
          return;
        }

        try {
          const response = await powerSkillService.getTestById(test.id);
          if (response.data.test.ai_generated) {
            setTestResult(response);
            clearInterval(intervalId);
          }
        } catch (err) {
          console.error("Auto refresh error:", err);
        }
      }, 5000); // Check every 5 seconds
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [test.id, test.ai_generated]);

  const handleRefreshData = async () => {
    setIsRefreshing(true);
    setError(null);
    try {
      const response = await powerSkillService.getTestById(test.id);
      setTestResult(response);
    } catch (err) {
      setError("Gagal memuat ulang data. Silakan coba lagi.");
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <div className="pom-result-container max-w-4xl mx-auto p-4 space-y-6 pb-20">
      <Card>
        <CardHeader className="border-b border-indigo-50 bg-indigo-50/30">
          <div className="flex justify-between items-center">
            <h5 className="text-lg font-bold text-indigo-900">
              Hasil Terakhir
            </h5>
            <span className="text-xs text-indigo-600 bg-indigo-100 px-2 py-1 rounded-full font-medium">
              {new Date(test.created_at).toLocaleDateString(
                "id-ID",
                {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                }
              )}
            </span>
          </div>
        </CardHeader>
      </Card>

      <Card className="border-orange-100 shadow-lg overflow-hidden">
        <CardHeader className="gradient-primary text-white p-8 text-center border-none">
          <div className="text-sm font-bold uppercase tracking-[0.3em] opacity-80 mb-2">
            Kategori Capaian Anda
          </div>
          <div className="mt-2 text-4xl font-black">{test.result_category}</div>
        </CardHeader>
      </Card>

      {!test.ai_generated ? (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex gap-4 items-start">
            <div className="bg-amber-100 p-3 rounded-full">
              <Hourglass className={`w-6 h-6 text-amber-600 animate-pulse`} />
            </div>
            <div>
              <h4 className="font-bold text-amber-900">
                Analisis sedang dalam proses
              </h4>
              <p className="text-amber-800 text-sm">
                Laporan mendalam sedang disiapkan oleh AI. Halaman akan diperbarui secara otomatis.
              </p>
            </div>
          </div>
          <Button
            onClick={handleRefreshData}
            disabled={isRefreshing}
            className="bg-amber-600 hover:bg-amber-700 text-white font-bold whitespace-nowrap"
          >
            {isRefreshing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              "Lihat Hasil"
            )}
          </Button>
        </div>
      ) : (
        <PowerSkillAiRecommendation testData={test} />
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 p-4 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          <p className="text-sm font-medium">{error}</p>
        </div>
      )}

      <Card className="border-gray-200">
        <button
          onClick={() => setShowAnswers(!showAnswers)}
          className="w-full flex justify-between items-center p-4 hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-2 font-bold text-gray-700">
            <CheckCircle2 className="w-5 h-5 text-green-500" />
            Detail Jawaban
          </div>
          {showAnswers ? <ChevronUp /> : <ChevronDown />}
        </button>
        {showAnswers && (
          <div className="border-t border-gray-100 max-h-96 overflow-y-auto p-4 space-y-2">
            {testResult.data.test_questions.map((item, idx) => (
              <div
                key={item.id}
                className="flex justify-between items-center p-3 bg-gray-50 rounded-lg border border-gray-100 text-sm"
              >
                <div className="flex gap-3">
                  <span className="font-bold text-gray-400">{idx + 1}.</span>
                  <span className="text-gray-700">{item.question}</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className={`font-black text-orange-600`}>
                    {getAnswerOptionLabels(item.answer)?.label}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <div className="flex flex-col sm:flex-row gap-4 pt-4">
        <Button
          outline
          onClick={onBackToHome}
        >
          Kembali ke Beranda
        </Button>
        <div className="flex-1">
          <PowerSkillPdfDownload testId={test.id} />
        </div>
      </div>
    </div>
  );
};

export default PowerSkillTestResult;
